import axios from "axios";

const normalizeBaseUrl = (url) => url.replace(/\/+$/, "");
const stripApiSuffix = (url) => url.replace(/\/api$/i, "");

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return stripApiSuffix(normalizeBaseUrl(envUrl.trim()));
  }
  if (process.env.NODE_ENV === "production") {
    return "https://yata-api.fly.dev";
  }
  return "http://localhost:5000";
};

const API_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const AUTH_SKIP_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/logout",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/reset-password",
];

const normalizeRequestPath = (url) => {
  if (!url) return "";
  try {
    if (url.startsWith("http")) {
      return new URL(url).pathname;
    }
  } catch (error) {
    // Ignore invalid URLs and fall back to raw string.
  }
  return url;
};

const shouldSkipAuth = (url) => {
  const rawPath = normalizeRequestPath(url);
  const normalizedPath = rawPath.startsWith("/api")
    ? rawPath.slice(4)
    : rawPath;
  return AUTH_SKIP_PATHS.some(
    (path) => normalizedPath === path || normalizedPath.startsWith(path + "/")
  );
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token && !shouldSkipAuth(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh (admin only)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !shouldSkipAuth(originalRequest?.url)
    ) {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const response = await api.post("/api/auth/refresh");
        const { accessToken } = response.data;

        // Store new token
        localStorage.setItem("accessToken", accessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to admin login
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userType");
        localStorage.removeItem("user");
        window.location.href = "/admin/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const authAPI = {
  // Admin auth
  adminLogin: (data) => api.post("/api/auth/login", data),
  adminRegister: (data) => api.post("/api/auth/register", data),
  adminLogout: () => api.post("/api/auth/logout"),
  adminRefresh: () => api.post("/api/auth/refresh"),
  adminForgotPassword: (email) =>
    api.post("/api/auth/forgot-password", { email }),
  adminResetPassword: (token, password) =>
    api.post("/api/auth/reset-password", { token, password }),
  adminChangePassword: (data) => api.put("/api/auth/change-password", data),
  getAdminProfile: () => api.get("/api/auth/me"),
};

export const adminAPI = {
  getDashboardStats: () => api.get("/api/admin/dashboard"),
  updateProfile: (data) => api.put("/api/auth/profile", data),
  requestEmailChange: (data) =>
    api.post("/api/auth/email-change/request", data),
  requestNewEmail: (data) => api.post("/api/auth/email-change/new-email", data),
  verifyEmailChange: (data) => api.post("/api/auth/email-change/verify", data),
  requestPasswordChange: (data) =>
    api.post("/api/auth/password-change/request", data),
  verifyPasswordChange: (data) =>
    api.post("/api/auth/password-change/verify", data),
};

export const coursesAPI = {
  getAll: (params) => api.get("/api/courses", { params }),
  getById: (id) => api.get(`/api/courses/${id}`),
  getAllAdmin: () => api.get("/api/courses/admin/all"),
  create: (data) =>
    api.post("/api/courses", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, data) =>
    api.put(`/api/courses/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/api/courses/${id}`),
};

export const instructorsAPI = {
  getAll: () => api.get("/api/instructors"),
  getAllAdmin: () => api.get("/api/instructors/admin/all"),
  create: (data) =>
    api.post("/api/instructors", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, data) =>
    api.put(`/api/instructors/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/api/instructors/${id}`),
};

export const statsAPI = {
  getPublic: () => api.get("/api/stats"),
};

export const linksAPI = {
  getPublic: () => api.get("/api/links/public"),
  resolve: (token) => api.get(`/api/links/resolve/${token}`),
};

export const contactAPI = {
  send: (data) => api.post("/api/contact", data),
};
